import { useQuery, UseQueryResult } from "@tanstack/react-query";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import saveAs from "file-saver";

const axiosInstance = createAxiosInstance(config.METRICS_API_BASE_URL);

export interface ILiveGatewayMetrics {
  id: string;
  type?: ConnectionType;
  accountName?: string;
  binds?: string;
  failed?: number;
  tps?: number;
  queue?: number;
  isUp?: boolean;
  /** Beta only: per-second counts over the last 10s */
  stats?: ILiveGatewayStats;
  /** Beta only, flattened from stats so they can be sorted */
  minTps?: number;
  maxTps?: number;
}

export interface ILiveGatewayStats {
  count: number;
  sum: number;
  min: number;
  average: number;
  max: number;
}

export interface IConnectionProfileFilter {
  id?: string;
  name?: string;
  type?: ConnectionType;
  ip?: string;
  password?: string;
  port?: string | number;
  liveOnly?: boolean;
  upOnly?: boolean;
  /** Beta metrics (10s window); omitted when off */
  beta?: boolean;
}

export enum ConnectionType {
  VENDOR,
  CLIENT,
}

export async function fetchLiveGatewayMetrics(
  params: Params<IConnectionProfileFilter>,
): Promise<IPageable<ILiveGatewayMetrics>> {
  try {
    const response = await axiosInstance.get("/live-gateway/get-metrics", {
      params,
    });

    // Get the list from API response
    const dataList: ILiveGatewayMetrics[] = (
      response.data as ILiveGatewayMetrics[]
    ).map((row) =>
      row.stats
        ? {
            ...row,
            tps: row.stats.average,
            minTps: row.stats.min,
            maxTps: row.stats.max,
          }
        : row,
    );

    // Parse sort parameter (format: "field,direction")
    let sortField: keyof ILiveGatewayMetrics | undefined;
    let sortDirection: "asc" | "desc" = "desc";

    if (params.sort) {
      const [field, direction] = params.sort.split(",");
      sortField = field as keyof ILiveGatewayMetrics;
      sortDirection = (direction as "asc" | "desc") || "desc";
    }

    // Sort the list based on the sort parameter
    const sortedList = [...dataList].sort((a, b) => {
      if (!sortField) return 0;

      const valueA = a[sortField];
      const valueB = b[sortField];

      // Handle null/undefined values
      if (valueA == null && valueB == null) return 0;
      if (valueA == null) return sortDirection === "asc" ? -1 : 1;
      if (valueB == null) return sortDirection === "asc" ? 1 : -1;

      // Compare values
      let comparison = 0;
      if (typeof valueA === "number" && typeof valueB === "number") {
        comparison = valueA - valueB;
      } else {
        // String comparison
        const strA = String(valueA).toLowerCase();
        const strB = String(valueB).toLowerCase();
        comparison = strA.localeCompare(strB);
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });

    // Apply pagination based on params
    const page = params.page ?? 0;
    // Use sortedList.length as default size to return all items if size not specified
    const size = params.size ?? sortedList.length;
    const startIndex = page * size;
    const endIndex = startIndex + size;
    const paginatedContent = sortedList.slice(startIndex, endIndex);

    // Construct pageable response
    const pageableResponse: IPageable<ILiveGatewayMetrics> = {
      content: paginatedContent,
      number: page,
      size: size,
      totalElements: sortedList.length,
      totalPages: Math.ceil(sortedList.length / size),
    };

    return pageableResponse;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error("The requested resource was not found.");
    }
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching accounts.",
    );
  }
}

/** Beta metrics window in seconds */
export const BETA_WINDOW_SECONDS = 10;

const REFRESH_INTERVAL_MS = 1000;
const BETA_REFRESH_INTERVAL_MS = BETA_WINDOW_SECONDS * 1000;

export const useLiveGatewayMetricsQuery = (
  params: Params<ILiveGatewayMetrics> & { beta?: boolean },
): UseQueryResult<IPageable<ILiveGatewayMetrics>, Error> => {
  const queryKey = ["liveGatewayMetrics", JSON.stringify(params)];
  return useQuery({
    queryKey,
    queryFn: () => fetchLiveGatewayMetrics(params),
    refetchInterval: params.beta
      ? BETA_REFRESH_INTERVAL_MS
      : REFRESH_INTERVAL_MS,
  });
};
export const exportSessions = async (
  params: Params<ILiveGatewayMetrics> = {},
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/live-gateway/csv", {
      params,
      responseType: "blob",
    });

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, "LiveGatewayMetrics.csv");

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error fetching LiveGatewayMetrics:",
      error.response?.data?.message || error.message || error,
    );
  }
};
